<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<div>

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" :editMode="dashboards.editMode">
				<GlyphIcon :icon="GlyphIconName['globe']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:6px;margin-top:1px">{{ data.title }}</div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<Button v-if="!dashboards.editMode && show && data.close === PbxAppDashboardItemWebClose.BUTTON" size="small" variant="text" class="header-button" @click="closeClicked()">
						<GlyphIcon :icon="GlyphIconName['times']" size="19" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="settings-box" style="padding-bottom:8px">
				<div>{{ t('web.panelName') }}</div>
				<InputText maxlength="64" size="small" v-model="settings.title" :required="true" fluid />

				<div class="mt-2 flex gap-1">
					<div class="w-1/2">
						<div>{{ t('web.width') }}</div>
						<InputGroup>
							<InputNumber size="small" v-model="settings.width" :useGrouping="false" :min="200" :max="2000" fluid />
							<InputGroupAddon>px</InputGroupAddon>
						</InputGroup>
						<div class="text-muted-color text-xs ml-2 mt-0.5">200 - 2000</div>
					</div>

					<div class="w-1/2">
						<div>{{ t('web.height') }}</div>
						<InputGroup>
							<InputNumber size="small" v-model="settings.height" :min="1" :max="2000" />
							<InputGroupAddon>px</InputGroupAddon>
						</InputGroup>
						<div class="text-muted-color text-xs ml-2 mt-0.5">100 - 2000</div>
					</div>
				</div>

				<div class="mt-2">{{ t('web.type') }}</div>
				<Select v-model="settings.mode" :options="modes" size="small" fluid option-value="value" option-label="label" />

				<div v-if="data.mode === PbxAppDashboardItemWebMode.RELOADED" class="mt-2">{{ t('web.interval') }}</div>
				<Select v-if="data.mode === PbxAppDashboardItemWebMode.RELOADED" v-model="settings.interval" :options="intervals" size="small" fluid option-value="value" option-label="label" />

				<div class="mt-2">{{ t('web.url') }}</div>
				<InputText type="url" size="small" v-model="settings.url" :required="true" placeholder="https://" fluid />
				<div class="text-muted-color text-xs ml-2 mt-0.5">{{  t('web.urlDescription') }}</div>

				<div class="mt-2">{{ t('web.closeContent') }}</div>
				<Select v-model="settings.close" :options="closings" option-value="value" option-label="label" size="small" fluid />

				<div class="flex justify-between items-center mt-2">
					<div>{{ t('web.ignoreLocalCalls') }}</div>
					<ToggleSwitch v-model="settings.ignoreLocal" size="small" />
				</div>

				<Message class="mt-2" severity="info">
					<div class="help-header">{{ $t('web.variableHelp') }}</div>
					<table style="font-size:11px">
						<tbody>
							<tr><th>%%A_NUMBER%%</th><td>=</td><td> {{ $t('web.aNumber') }}</td></tr>
							<tr><th>%%A_NUMBER_NO_PFX%</th><td>=</td><td> {{ $t('web.aNumberNoPrefix') }}</td></tr>
							<tr><th>%%B_NUMBER%%</th><td>=</td><td> {{ $t('web.bNumber') }}</td></tr>
							<tr><th>%%B_NUMBER_NO_PFX%%</th><td>=</td><td> {{ $t('web.bNumberNoPrefix') }}</td></tr>
							<tr><th>%%A_NAME%%</th><td>=</td><td> {{ $t('web.aName') }}</td></tr>
							<tr><th>%%B_NAME%%</th><td>=</td><td> {{ $t('web.bName') }}</td></tr>
							<tr><th>%%VARIABLE%%</th><td>=</td><td> {{ $t('web.variable') }}</td></tr>
							<tr><th>%%TCXTOKEN%%</th><td>=</td><td> {{ $t('web.tcxtoken') }}</td></tr>
							<tr><th>%%EMAIL%%</th><td>=</td><td> {{ $t('web.email') }}</td></tr>
							<tr><th>%%OTHER%%</th><td>=</td><td> {{ $t('web.other') }}</td></tr>
							<tr><th style="vertical-align:top">%%OTHER_NO_PFX%%</th><td style="vertical-align:top">=</td><td> {{ $t('web.otherNoPrefix') }}</td></tr>
						</tbody>
					</table>
				</Message>

			</div>

			<!-- VIEW MODE -->
			<div class="settings-box" v-if="!dashboards.editMode">
				<iframe v-show="show" :height="data.height-35" :width="data.width-20" style="border:0" :id="frameId" title="3de parts indhold" />
			</div>
		</div>
	</PanelDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useEmployeesStore } from '@/stores/Employees'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import { PbxAppDashboardItemWebClose, PbxAppDashboardItemWebMode, type PbxAppDashboardItemWeb, type PbxAppEmployeesExtensionCallWithDate } from '@/TcxApi/PbxApp'
import { onUnmounted, ref } from 'vue'
import { PbxEventType, type PbxEventCall } from '@per_moeller/tcx-realtime-client'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

const ss = useSettingsStore()
const dashboards = useDashboardsStore()
const employees = useEmployeesStore()
const realtime = useRealtimeStore()
const api = useApiStore()
const { t } = useI18n()

interface Props {
	data: PbxAppDashboardItemWeb
}

const props = defineProps<Props>()
const isDraggable = ref(false)
const modes = [
	{ value: 'STATIC', label: t('web.types.static') },
	{ value: 'RELOADED', label: t('web.types.reloaded') },
	{ value: 'RINGING', label: t('web.types.ringing') },
	{ value: 'ANSWERED', label: t('web.types.answered') },
	{ value: 'RINGING_OR_OUT', label: t('web.types.callRing') },
	{ value: 'ANSWERED_OR_OUT', label: t('web.types.callAnswer') },
]
const intervals = [
	{ value: 0, label: t('web.intervals.0') },
	{ value: 10, label: t('web.intervals.10') },
	{ value: 30, label: t('web.intervals.30') },
	{ value: 45, label: t('web.intervals.45') },
	{ value: 60, label: t('web.intervals.60') },
	{ value: 120, label: t('web.intervals.120') },
	{ value: 300, label: t('web.intervals.300') },
	{ value: 600, label: t('web.intervals.600') },
	{ value: 1800, label: t('web.intervals.1800') },
	{ value: 3600, label: t('web.intervals.3600') },
	{ value: 7200, label: t('web.intervals.7200') }
]
const closings = [
	{ value: 'NEVER', label: t('web.closeContents.never') },
	{ value: 'ON_END', label: t('web.closeContents.onEnd') },
	{ value: 'BUTTON', label: t('web.closeContents.button') }
]
const timer = ref<ReturnType<typeof setInterval> | undefined>(undefined)
const frameId = ref<string | undefined>(undefined)
const show = ref(false)
const currentCall = ref<PbxAppEmployeesExtensionCallWithDate>()
const settings = dashboards.dashboard?.items.find(item => item.column === props.data.column && item.position == props.data.position) as PbxAppDashboardItemWeb

function remove() {
	dashboards.removeItem(props.data.column, props.data.position)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

function callInOrOut(event: PbxEventCall) {
	if (['RINGING_OR_OUT', 'ANSWERED_OR_OUT'].includes(props.data.mode) && employees.me && event.extension === employees.me.primaryExtension?._id && event.device === ss.realPhoneId) {
		if (
			(event.leg === 'I' && event.event === PbxEventType.CALL_START) || // User is making a outbound call
			(event.leg === 'O' && props.data.mode === PbxAppDashboardItemWebMode.RINGING_OR_OUT && event.event === PbxEventType.CALL_RING) || // User got incoming ringing call
			(event.leg === 'O' && props.data.mode === PbxAppDashboardItemWebMode.ANSWERED_OR_OUT && event.event === PbxEventType.CALL_ANSWER)) { // User got incoming answered call
			const otherParty = event.leg === 'I' ? event.callee : event.caller

			if (props.data.ignoreLocal && otherParty && (otherParty[0] !== '+' || otherParty.length < 8)) { return }

			setTimeout(() => { // To ensure employees has been updated with the call
				// Find calls that concerns my device only
				if (employees.me?.primaryExtension) {
					const myCalls = employees.me.primaryExtension.calls.filter(call => call.device === ss.realPhoneId)
					if (myCalls.length === 1) { // If I have exactly 1 incoming call in the right state on the phone that I am using - then we have a call that we need to care about
						show.value = true
						currentCall.value = myCalls[0]
						loadWeb(myCalls[0])
					}
				}
			}, 50)
		}
	}
}

function callRing(event: PbxEventCall) {
	if (props.data.mode === PbxAppDashboardItemWebMode.RINGING && employees.me && event.extension === employees.me.primaryExtension?._id && event.device === ss.realPhoneId && event.leg === 'O') {
		if (props.data.ignoreLocal && event.caller && (event.caller[0] !== '+' || event.caller.length < 8)) { return }

		setTimeout(() => { // To ensure employees has been updated with the call
			if (employees.me?.primaryExtension) {
				// Find calls that concerns my device only
				const myCalls = employees.me.primaryExtension.calls.filter(call => call.device === ss.realPhoneId)

				if (myCalls.length === 1) { // If I have exactly 1 incoming call in the right state on the phone that I am using - then we have a call that we need to care about
					show.value = true
					currentCall.value = myCalls[0]
					loadWeb(myCalls[0])
				}
			}
		}, 50)
	}
}

function callAnswer(event: PbxEventCall) {
	if (!employees.me) { return }

	if (props.data.mode === PbxAppDashboardItemWebMode.ANSWERED && employees.me && event.extension === employees.me.primaryExtension?._id && event.device === ss.realPhoneId && event.leg === 'O') {
		if (props.data.ignoreLocal && event.caller && (event.caller[0] !== '+' || event.caller.length < 8)) { return }

		setTimeout(() => { // To ensure that employees has been updated with the call
			if (employees.me?.primaryExtension) {
				const myCalls = employees.me.primaryExtension.calls.filter(call => call.device === ss.realPhoneId)

				if (myCalls.length === 1) { // If I have exactly 1 incoming call in the right state on the phone that I am using - then we have a call that we need to care about
					show.value = true
					currentCall.value = myCalls[0]
					loadWeb(myCalls[0])
				}
			}
		}, 50)
	}
}

function callEnd(event: PbxEventCall) {
	if (props.data.close !== PbxAppDashboardItemWebClose.ON_END || !currentCall.value || !frameId.value) { return }

	if (['RINGING', 'ANSWERED', 'RINGING_OR_OUT', 'ANSWERED_OR_OUT'].includes(props.data.mode) && employees.me && event.extension === employees.me.primaryExtension?._id && event.device === ss.realPhoneId && event.eventId === currentCall.value._id) {
		show.value = false
		currentCall.value = undefined

		const iframe = document.getElementById(frameId.value) as HTMLIFrameElement

		if (iframe) {
			iframe.src = ''
		}
	}
}

function closeClicked() {
	if (!frameId.value) { return }

	show.value = false
	const iframe = document.getElementById(frameId.value) as HTMLIFrameElement

	if (iframe) {
		iframe.src = ''
	}
}

async function loadWeb(call?: PbxAppEmployeesExtensionCallWithDate) {
	if (!frameId.value) { return }

	const iframe = document.getElementById(frameId.value) as HTMLIFrameElement

	if (iframe) {
		let url = settings.url

		if (call) {
			const otherParty = call.leg == 'I' ? call.callee.number : call.caller.number

			url = url
				.replace('%%A_NUMBER%%', call.caller.number)
				.replace('%%A_NUMBER_NO_PFX%%', ss.cutNationalPrefix(call.caller.number))
				.replace('%%B_NUMBER%%', call.callee.number)
				.replace('%%B_NUMBER_NO_PFX%%', ss.cutNationalPrefix(call.callee.number))
				.replace('%%A_NAME%%', encodeURIComponent(call.caller.name ?? ''))
				.replace('%%B_NAME%%', encodeURIComponent(call.callee.name ?? ''))
				.replace('%%TCXTOKEN%%', ss.token as string)
				.replace('%%EMAIL%%', employees.me?.emailAddress ?? '')
				.replace('%%OTHER%%', otherParty)
				.replace('%%OTHER_NO_PFX%%', ss.cutNationalPrefix(otherParty))

			const variables = url.match(/%%[^%]+%%/g)

			if (variables) {
				for (const variable of variables) {
					try {
						const res = await api.get<{ name: string, value: string | null }>(`/pbx/variable/${call._id}/${variable.replace(/%/g, '')}`)
						if (res.data.value) {
							url.replace(variable, res.data.value)
						} else {
							url.replace(variable, '')
						}
					} catch (_) {}
				}
			}
		}

		iframe.src = url
	}
}

frameId.value = `webframe${Math.random().toString()}`

realtime.pbxEvents.on('CALL_RING', callRing)
realtime.pbxEvents.on('CALL_RING', callInOrOut)
realtime.pbxEvents.on('CALL_START', callInOrOut)
realtime.pbxEvents.on('CALL_ANSWER', callAnswer)
realtime.pbxEvents.on('CALL_ANSWER', callInOrOut)
realtime.pbxEvents.on('CALL_END', callEnd)

if (settings.width < ss.settings.dashboardColumnWidth) {
	settings.width = ss.settings.dashboardColumnWidth
}

switch (props.data.mode) {
	case PbxAppDashboardItemWebMode.RELOADED:
		timer.value = setInterval(() => {
			loadWeb()
		}, props.data.interval * 1000)

		show.value = true

		setTimeout(() => { loadWeb() }, 1)
		break

	case PbxAppDashboardItemWebMode.STATIC:
		show.value = true
		setTimeout(() => { loadWeb() }, 1)
		break
}

onUnmounted(() => {
	if (timer.value) {
		clearInterval(timer.value)
		timer.value = undefined
	}
	realtime.pbxEvents.off('CALL_RING', callRing)
	realtime.pbxEvents.off('CALL_RING', callInOrOut)
	realtime.pbxEvents.off('CALL_START', callInOrOut)
	realtime.pbxEvents.off('CALL_ANSWER', callAnswer)
	realtime.pbxEvents.off('CALL_ANSWER', callInOrOut)
	realtime.pbxEvents.off('CALL_END', callEnd)
})
</script>

<style scoped>
	.settings-box {
		margin: 5px 10px;
	}
	.help-header {
		margin-left: -8px;
		margin-right: -8px;
		margin-top: -5px;
		padding: 5px 4px;
		font-weight: bold;
		background: #ffffff;
		border-radius: 5px;
		margin-bottom: 3px;
	}

	@media(prefers-color-scheme: dark) {
		.help-header {
			background: #111;
		}
	}
</style>
